import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-admin-validation-rule-edit',
    templateUrl: './admin-validation-rule-edit.component.html',
    styleUrls: ['./admin-validation-rule-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminValidationRuleEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  modalTitle = 'Create Rule / Edit Rule';

  filterParams: any = {};
  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  private isInitialized = false;
  isLoading = false;
  ruleModel: any = {};
  step = 1;
  clientKey: any = '';
  isNewRule = true;
  allLinkedActions: any[] = [];
  allLinkedCacheObjects: any[] = [];
  newActionKey = '';
  newCacheObjectKey = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  /**
   * Merge the standard global context params (tax_year, scenario, jcd_key,
   * scenarioCode, is_issue_key_enabled, ...) into an action payload. The
   * backend requires them on the rule action / cache calls; AuthInterceptor
   * only enriches `saveJsonObject` URLs, so the `loadJsonObject` calls
   * (zxs8ob/g1pp14/cl2s8u/dszxm0) need them merged here. Action-specific
   * params take precedence over global ones.
   */
  private withGlobalParams(params: Record<string, any>): Record<string, any> {
    const globalParams = extractObject(this.globalService) || {};
    const merged: Record<string, any> = {};
    Object.keys(globalParams).forEach((key) => {
      const value = globalParams[key];
      if (value !== null && value !== undefined) {
        merged[key] = value;
      }
    });
    return { ...merged, ...params };
  }

  /**
   * Identifier keys can legitimately be 0. Distinguish "missing"
   * (null/undefined/'') from a valid 0 (Admin migration guide Pitfall 9, Variant B).
   */
  private hasKey(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.globalService || !this.GENERAL_CONFIG) return;
    this.isInitialized = true;
    this.clientKey = this.rowData?.clientKey ?? this.filterParams?.client_key ?? '';
    this.isNewRule = this.rowData?.isNewRule !== false;
    const currentRule = this.rowData?.currentRule || {};
    this.ruleModel = {
      RULE_KEY: this.isNewRule ? '' : currentRule.RULE_KEY,
      RULE_NAME: this.isNewRule ? '' : currentRule.RULE_NAME,
      RULE_DESC: this.isNewRule ? '' : currentRule.RULE_DESC,
      RULE_PATH: this.isNewRule ? '' : currentRule.RULE_PATH,
      RULE_ACTIVE_FLAG: this.isNewRule ? '1' : (currentRule.RULE_ACTIVE_FLAG || '1')
    };
    this.showFormLoader = false;
    if (!this.isNewRule && this.ruleModel.RULE_KEY) {
      this.getRuleLinkedAction();
    }
    this.cdr.detectChanges();
  }

  nextStep(): void {
    this.step = this.step < 3 ? this.step + 1 : 3;
  }

  prevStep(): void {
    this.step = this.step > 1 ? this.step - 1 : 1;
  }

  save(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.isSaveClicked = true;
    const params = this.withGlobalParams({
      client_key: this.clientKey,
      clob_data: JSON.stringify([{ ...this.ruleModel }]),
      url: this.isNewRule ? 'dszxm0' : 'yublv5'
    });
    const endpoint = this.isNewRule
      ? `${this.GENERAL_CONFIG.base_url}/loadJsonObject?action_code=dszxm0`
      : `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=yublv5`;
    this.http.post<any>(endpoint, params, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.isLoading = false;
        this.isSaveClicked = false;
        if (data?.callSuccess === '1') {
          if (this.isNewRule && data?.jsonObject?.[0]?.VALIDATION_RULE_KEY) {
            this.ruleModel.RULE_KEY = data.jsonObject[0].VALIDATION_RULE_KEY;
            this.isNewRule = false;
          }
          this.gtwSnackbarService.openSnackBar('Validation rule saved successfully', 'gtw-snackbar--green');
          this.step = 2;
          this.getRuleLinkedAction();
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Unable to save validation rule', 'gtw-snackbar--yellow');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Unable to save validation rule', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  private getRuleLinkedAction(): void {
    if (!this.hasKey(this.ruleModel?.RULE_KEY)) return;
    this.isLoading = true;
    // Legacy GENERAL_CONFIG.common_api_url maps to the new batch_api_url; the
    // global GENERAL_CONFIG (= environment) does not expose common_api_url.
    const commonApiUrl = this.GENERAL_CONFIG.common_api_url
      ?? this.GENERAL_CONFIG.batch_api_url
      ?? this.GENERAL_CONFIG.base_url;
    const url = `${commonApiUrl}/getDroolsRulesConfigDataByClient`;
    const params = { client_key: this.clientKey, rule_key: this.ruleModel.RULE_KEY };
    this.http.post<any>(url, params, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.isLoading = false;
        this.allLinkedActions = [];
        this.allLinkedCacheObjects = [];
        if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
          data.jsonObject.forEach((rule: any) => {
            if (String(rule?.validation_rule_key) === String(params.rule_key)) {
              this.allLinkedActions = rule?.validation_rule_actions || [];
              this.allLinkedCacheObjects = rule?.validation_rule_cache_objects || [];
            }
          });
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  addAction(): void {
    if (!this.hasKey(this.newActionKey) || !this.hasKey(this.ruleModel?.RULE_KEY)) return;
    this.isLoading = true;
    const url = `${this.GENERAL_CONFIG.base_url}/loadJsonObject?action_code=zxs8ob`;
    const params = this.withGlobalParams({
      new_action_key: this.newActionKey,
      action_key: this.newActionKey,
      client_key: this.clientKey,
      validation_rule_key: this.ruleModel.RULE_KEY
    });
    this.http.post<any>(url, params, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.isLoading = false;
        if (data?.callSuccess === '1') {
          this.newActionKey = '';
          this.getRuleLinkedAction();
        }
      },
      () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  delinkAction(action: any): void {
    if (!this.hasKey(action?.action_validation_key)) return;
    this.isLoading = true;
    const url = `${this.GENERAL_CONFIG.base_url}/saveJsonObject?action_code=nrvl3o`;
    // Legacy toggles lnk_or_d_lnk by current status: active (status==1) -> 0 (delink),
    // inactive (status==0) -> 1 (link). The backend rejects non-numeric values like 'D'.
    const lnkOrDlnk = String(action?.action_validation_status) === '1' ? 0 : 1;
    const params = this.withGlobalParams({
      rule_action_key: action.action_validation_key,
      client_key: this.clientKey,
      lnk_or_d_lnk: lnkOrDlnk
    });
    this.http.post<any>(url, params, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.isLoading = false;
        this.getRuleLinkedAction();
      },
      () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  addCacheObject(): void {
    if (!this.hasKey(this.newCacheObjectKey) || !this.hasKey(this.ruleModel?.RULE_KEY)) return;
    this.isLoading = true;
    const url = `${this.GENERAL_CONFIG.base_url}/loadJsonObject?action_code=g1pp14`;
    const params = this.withGlobalParams({
      new_cache_key: this.newCacheObjectKey,
      url: 'g1pp14',
      cache_key: this.newCacheObjectKey,
      client_key: this.clientKey,
      validation_rule_key: this.ruleModel.RULE_KEY
    });
    this.http.post<any>(url, params, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.isLoading = false;
        if (data?.callSuccess === '1') {
          this.newCacheObjectKey = '';
          this.getRuleLinkedAction();
        }
      },
      () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  delinkCacheObject(cache: any): void {
    if (!this.hasKey(cache?.drools_cache_key) || !this.hasKey(this.ruleModel?.RULE_KEY)) return;
    this.isLoading = true;
    const url = `${this.GENERAL_CONFIG.base_url}/loadJsonObject?action_code=cl2s8u`;
    const params = this.withGlobalParams({
      drools_cache_key: cache.drools_cache_key,
      validation_rule_key: this.ruleModel.RULE_KEY,
      client_key: this.clientKey
    });
    this.http.post<any>(url, params, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.isLoading = false;
        this.getRuleLinkedAction();
      },
      () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
